import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../lib/AuthContext';
import { api, errMsg } from '../lib/api';

export default function ForgotPasswordPage() {
  const { config } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center px-4 bg-[radial-gradient(1200px_circle_at_20%_0%,rgba(14,165,164,0.10),transparent_55%),radial-gradient(900px_circle_at_90%_10%,rgba(253,231,179,0.30),transparent_50%)]">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <span className="inline-flex h-12 w-12 rounded-2xl bg-[hsl(var(--primary))] text-white items-center justify-center shadow-[var(--shadow-soft)]">
            <Wrench className="h-6 w-6" />
          </span>
          <h1 className="text-2xl font-semibold font-display">Forgot password</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Enter your account email and we'll send you a reset link.
          </p>
        </div>
        <Card className="rounded-2xl shadow-[var(--shadow-card)] border-black/5">
          <CardContent className="pt-6">
            {config && !config.password_reset_enabled ? (
              <p className="text-sm text-center">
                Password reset by email is coming soon. For now, please contact us and we'll help you regain access:
                {' '}<span className="font-medium">{config.platform_mobile}</span>
                {config.platform_email ? <> / <span className="font-medium">{config.platform_email}</span></> : null}.
              </p>
            ) : sent ? (
              <div className="space-y-3 text-center">
                <p className="text-sm">
                  If an account exists for <span className="font-medium">{email}</span>, a reset link is on its way.
                  Check your inbox and spam folder — the link expires in 60 minutes.
                </p>
                <Button variant="outline" className="w-full h-12 rounded-xl" onClick={() => setSent(false)}>
                  Send again
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <Input data-testid="forgot-email-input" type="email" required placeholder="Email"
                  value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl" />
                <Button data-testid="forgot-submit-button" type="submit" disabled={busy} className="w-full h-12 rounded-xl text-base">
                  {busy ? 'Sending…' : 'Send reset link'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
        <p className="text-center text-sm text-[hsl(var(--muted-foreground))]">
          Remembered it?{' '}
          <Link to="/login" className="font-medium text-[hsl(var(--primary))]">Back to log in</Link>
        </p>
      </div>
    </div>
  );
}
